import { bridge } from "@multiweb/reactCore/bridge";
import { Activity, ActivityType } from "./Activity";
import { DefinitionParticipant } from "./Participants";
import { Point } from "./Point";
import { Position } from "./Position";
import { Set } from "./Set";
import { Transition } from "./Transition";
import { Menu, Trigger } from "./Triggers";
import { WorkflowStatus } from "./WorkflowStatus";

const { generateUUID } = bridge;

/** Udává aktuální verzi WorkflowDefinition. */
const ACTUAL_DEFINITION_VERSION = 9.03; //9.0f;// 8.03f;// 3.01f;
/** Šířka normální aktivity. */
const NOMRAL_ACTIVITY_WIDTH = 100;
/** Šířka startovací/konečné aktivity. */
const START_END_ACTIVITY_WIDTH = 60;
/** Výška aktivity. */
const ACTIVITY_HEIGHT = 60;
/** Poziční offset prvků workflow, pokud dochází k duplikaci v rámci téže sady. */
const DUPLICATION_OFFSET = 20;

/** Pomocný typ, který propojení přidává navíc i ID počáteční aktivity. */
type FromTransition = {
    fromId: string;
    transition: Transition;
};

export class Workflow {
    public version: string;
    public definitionVersion: string;
    public identifier: string;
    public className: string;

    public sets: Set[];
    public activities: Activity[];
    public definitionParticipants: DefinitionParticipant[];

    constructor(version: string, definitionVersion: string, identifier: string, className: string, sets: Set[], activities: Activity[], definitionParticipants: DefinitionParticipant[]) {
        this.version = version;
        this.definitionVersion = definitionVersion === "" ? ACTUAL_DEFINITION_VERSION.toString() : definitionVersion;
        this.identifier = identifier;
        this.className = className;
        this.sets = sets;
        this.activities = activities;
        this.definitionParticipants = definitionParticipants;
    }

    public get isAnyActivtyOfStartType(): boolean {
        return this.activities.some(activity => activity.type === ActivityType.start);
    }

    public get isAnyActivtyOfEndType(): boolean {
        return this.activities.some(activity => activity.type === ActivityType.end);
    }

    /** Vrací seznam akttivit, které se nenacházejí na aktuálně zvolené sadě. */
    public getExistingActivities(setId: string): Activity[] {
        return this.activities.filter(activity => activity.positions.every(position => position.set.id !== setId));
    }

    public addSet(name: string, description: string, id?: string): void {
        this.sets.push(new Set(name, description, this.sets.length, id));
    }

    public removeSet(setId: string): void {
        if (this.sets.length === 1) // Měla by být dostupná aspoň jedna sada vždy
            return;

        this.sets = this.sets.filter(set => set.id !== setId);

        const activitiesIndexesToRemove: number[] = [];

        // Z každé aktivity musíme odebrat referenci na sadu, kterou mažeme
        for (let i = 0; i < this.activities.length; i++) {
            const filteredTransitions = this.activities[i].transitions.filter(transition => transition.setId !== setId);
            this.activities[i].transitions = filteredTransitions;

            const filteredPositions = this.activities[i].positions.filter(position => position.set.id !== setId);
            this.activities[i].positions = filteredPositions;

            if (filteredPositions.length === 0)
                activitiesIndexesToRemove.push(i);
        }

        // Odebíráme aktivity, které by už nebyly v žádné sadě
        this.activities = this.activities.filter((_activity, index) => !activitiesIndexesToRemove.includes(index));
    }

    public addActivity(activityType: ActivityType, setId: string, left: number, top: number): string {
        const newActivityId = this._getNewId();
        const width = activityType === ActivityType.normal ? NOMRAL_ACTIVITY_WIDTH : START_END_ACTIVITY_WIDTH;
        const position = new Position(left, top, width, ACTIVITY_HEIGHT, this.sets.find(set => set.id === setId)!);

        const newActivity = new Activity(newActivityId, activityType, [], [], [], [], [position]);
        newActivity.name = newActivityId.toString();
        this.activities.push(newActivity);

        return newActivityId;
    }

    public addTransition(from: string, to: string, setId: string): string {
        if (from === to)
            return this._createSelfReferencingTransition(from, setId);

        const fromActivity = this.activities.find(activity => activity.id === from);
        const toActivity = this.activities.find(activity => activity.id === to);

        if (fromActivity == null || toActivity == null)
            throw new Error("Activity does not exist!");

        const fromPosition = fromActivity.positions.find(position => position.set.id === setId);
        const toPosition = toActivity.positions.find(position => position.set.id === setId);

        if (fromPosition == null || toPosition == null)
            throw new Error("Activity does not have position in given set!");

        const points: Point[] = [
            { x: fromPosition.x + fromPosition.width / 2, y: fromPosition.y + fromPosition.height / 2 },
            { x: toPosition.x + toPosition.width / 2, y: toPosition.y + toPosition.height / 2 },
        ];

        const newId = this._getNewId();
        const newTransition = new Transition(newId, to, points, setId, []);

        fromActivity.transitions.push(newTransition);

        return newTransition.id;
    }

    /**
     * Funkce vymaže aktivitu. Pokud bude aktivita pouze v jedné sadě nebo se neuvede parametr @param setId,
     * tak se provede smazání z celého workflow. Jinak se smaže pouze reference v dané sadě.
     * @param activityId Id aktivity ke smazání.
     * @param setId Id sady, ze které se má aktivita smazat (smaže se z celého workflow, pokud parametr nebude uveden).
     */
    public deleteActivity(activityId: string, setId?: string): void {
        const activity = this.activities.find(act => act.id === activityId);

        if (activity == null)
            throw new Error(`Activity with id '${activityId}' does not exist! Could not delete it.`);

        if (setId === undefined || activity.positions.length <= 1) {
            this._deleteActivityFromWorkflow(activityId);
            return;
        }

        this._deleteActivityFromSet(activity, setId);
    }

    public deleteTransition(activityId: string, transitionId: string): void {
        const activity = this.activities.find(act => act.id === activityId);

        if (activity == null)
            throw new Error("Activity does not exist!");

        activity.transitions = activity.transitions.filter(transition => transition.id !== transitionId);
    }

    /**
     * Duplikuje seznam aktivit včetně propojení, která jsou mezi nimi.
     * @param activitiesIds Seznam aktivit ke zduplikování.
     * @param destinationSetId ID cílové sady, do které se mají objekty zduplikovat.
     * @param originalSetId ID sady ze které se duplikuje.
     * @returns ID první zduplikované aktivity.
     */
    public duplicateActivities(activitiesIds: readonly string[], destinationSetId: string, originalSetId: string): string {
        const destinationSet = this.sets.find(set => set.id === destinationSetId);
        const activitiesToDuplicate = this.activities.filter(act => activitiesIds.includes(act.id) && act.type !== ActivityType.start);
        const transitionsToDuplicate: FromTransition[] = [];

        if (destinationSet == null)
            throw new Error(`Set "${destinationSetId}" does not exist!`);

        for (const activity of activitiesToDuplicate) {
            for (const transition of activity.transitions) {
                if (activitiesToDuplicate.some(act => act.id === transition.to))
                    transitionsToDuplicate.push({ fromId: activity.id, transition });
            }
        }

        const duplicatedActivities = activitiesToDuplicate.map(
            activityToDuplicate => this._duplicateActivity(activityToDuplicate, destinationSet, originalSetId),
        );

        this._duplicateTransitions(transitionsToDuplicate, activitiesToDuplicate, duplicatedActivities, destinationSetId);

        return duplicatedActivities[0].id;
    }

    public removeDefinitionParticipant(defParticipantIdentifier: string): void {
        // odebrání referencí na definičního hráče z aktivit
        this.activities.forEach(activity => {
            activity.participants = activity.participants.filter(participant => {
                if (participant.constructor !== DefinitionParticipant)
                    return true;

                return (participant as DefinitionParticipant).identifier !== defParticipantIdentifier;
            });
        });

        // odebrání samotného definičního hráče
        this.definitionParticipants = this.definitionParticipants.filter(defParticipant => defParticipant.identifier !== defParticipantIdentifier);
    }

    public applyWorkflowStatus(wfStatus: WorkflowStatus): void {
        const isWfEnded = wfStatus.isWfEnded;

        if (isWfEnded) {
            this.activities.forEach(activity => {
                if (activity.type === ActivityType.end)
                    activity.isActive = true;
            });
            return;
        }

        this.activities.forEach(activity => {
            const foundActiveActivity = wfStatus.activeActivities.find(aa => aa.activityId === activity.id);

            if (foundActiveActivity) {
                activity.isActive = true;
                activity.activeParticipants = foundActiveActivity.activeParticipants;
            }

            activity.transitions.forEach(transition => {
                wfStatus.activeActivities.forEach(aa => {
                    if (aa.activityId === transition.to && aa.parentActivityId === activity.id)
                        transition.isActive = true;

                    if (aa.activatedInputTransitionIds.includes(transition.id))
                        transition.isActive = true;
                });
            });
        });
    }

    private _duplicateActivity(activity: Activity, set: Set, originalSetId: string): Activity {
        const width = activity.type === ActivityType.normal ? NOMRAL_ACTIVITY_WIDTH : START_END_ACTIVITY_WIDTH;
        const oldPosition = activity.positions.find(position => position.set.id === originalSetId);

        if (!oldPosition)
            throw new Error("No old position of activity found!");

        const newX = set.id === originalSetId ? oldPosition.x + DUPLICATION_OFFSET : oldPosition.x;
        const newY = set.id === originalSetId ? oldPosition.y + DUPLICATION_OFFSET : oldPosition.y;
        const newPosition = new Position(newX, newY, width, ACTIVITY_HEIGHT, set);
        const duplicatedTriggers = this._duplicateTriggers(activity.triggers);
        const newDuplicatedActivity = new Activity(this._getNewId(), activity.type, activity.actions, activity.participants, duplicatedTriggers, [], [newPosition]);

        const { name, showInWfHistory, killChildren, description, inheritParticipants, condition, runCondition, order, inputTransType, outputTransType } = activity;
        newDuplicatedActivity.name = name;
        newDuplicatedActivity.showInWfHistory = showInWfHistory;
        newDuplicatedActivity.killChildren = killChildren;
        newDuplicatedActivity.description = description;
        newDuplicatedActivity.inheritParticipants = inheritParticipants;
        newDuplicatedActivity.condition = condition;
        newDuplicatedActivity.runCondition = runCondition;
        newDuplicatedActivity.order = order;
        newDuplicatedActivity.inputTransType = inputTransType;
        newDuplicatedActivity.outputTransType = outputTransType;
        this.activities.push(newDuplicatedActivity);

        return newDuplicatedActivity;
    }

    private _duplicateTransitions(transToDuplicate: FromTransition[], activitiesToDuplicate: Activity[], duplicatedActivities: Activity[], destSetId: string): void {
        if (transToDuplicate.length === 0)
            return;

        for (let i = 0; i < activitiesToDuplicate.length; i++) {
            const duplicableTransitions = transToDuplicate.filter(tran => tran.fromId === activitiesToDuplicate[i].id);

            if (duplicableTransitions.length === 0)
                continue;

            duplicatedActivities[i].transitions = duplicableTransitions.map(({ transition }) => {
                const adequateIndex = activitiesToDuplicate.findIndex(act => act.id === transition.to);
                return this._duplicateTransition(transition, destSetId, duplicatedActivities[adequateIndex].id);
            });
        }
    }

    private _duplicateTransition(transition: Transition, destSetId: string, to: string): Transition {
        const newPoints = destSetId === transition.setId
            ? transition.points.map(point => new Point(point.x + DUPLICATION_OFFSET, point.y + DUPLICATION_OFFSET)) : transition.points;
        const duplicatedTransition = new Transition(this._getNewId(), to, newPoints, destSetId, transition.actions);
        const { name, condition } = transition;
        duplicatedTransition.name = name;
        duplicatedTransition.condition = condition;

        return duplicatedTransition;
    }

    private _duplicateTriggers(oldTriggers: readonly Trigger[]): Trigger[] {
        return oldTriggers.map(trigger => {
            if (trigger instanceof Menu)
                return new Menu(generateUUID(), trigger.codename, trigger.iconName, trigger.participantsSelecting.toString(), trigger.multiActionEnable.toString(), trigger.validatePostReq.toString(), trigger.validateRecommended.toString(), trigger.chooseFromUsersDefault.toString(), trigger.masterNode, trigger.paramFormName, trigger.linkAttrForSelObj, trigger.setValues, trigger.captions, trigger.confirmMessages);
            return trigger;
        });
    }

    private _deleteActivityFromSet(activity: Activity, setId: string): void {
        //musím smazat příslušnou Transition a Position
        const transitions = activity.transitions.filter(transition => transition.setId !== setId);
        activity.transitions = transitions;

        const positions = activity.positions.filter(position => position.set.id !== setId);
        activity.positions = positions;

        //musím vymazat i vazby jiných aktivit na danou aktivitu v dané sadě
        for (let i = 0; i < this.activities.length; i++) {
            const filteredTransitions = this.activities[i].transitions.filter(transition => transition.setId !== setId || transition.to !== activity.id);
            this.activities[i].transitions = filteredTransitions;
        }
    }

    private _deleteActivityFromWorkflow(activityId: string): void {
        //musím smazat příslušnou Activity
        this.activities = this.activities.filter(activity => activity.id !== activityId);

        //musím vymazat i vazby jiných aktivit na danou aktivitu ve všech sadách
        for (let i = 0; i < this.activities.length; i++) {
            const filteredTransitions = this.activities[i].transitions.filter(transition => transition.to !== activityId);
            this.activities[i].transitions = filteredTransitions;
        }
    }

    /** Vytvoří propojení, které vede do stejné aktivity, ze které bylo vytvořeno. */
    private _createSelfReferencingTransition = (activityId: string, setId: string): string => {
        const activity = this.activities.find(activity => activity.id === activityId);

        if (activity == null)
            throw new Error("Activity does not exist!");

        const position = activity.positions.find(position => position.set.id === setId);

        if (position == null)
            throw new Error("Activity does not have poisition in given set!");

        const points: Point[] = [
            { x: position.x + 20, y: position.y + 40 },
            { x: position.x + position.width - 20, y: position.y + 40 },
        ];

        const newId = this._getNewId();
        const newTransition = new Transition(newId, activity.id, points, setId, []);
        activity.transitions.push(newTransition);

        return newTransition.id;
    };

    /** Strašná metoda získávání id ze silverlightu, nutná kvůli zpětné kompatibilitě. Společné jak pro transition, tak pro activity */
    private _getNewId(): string {
        if (this.activities.length === 0)
            return "ID_1";

        let highestNumber = 1;

        this.activities.forEach(activity => {
            const idNumber = Number(activity.id.replace("ID_", ""));
            if (idNumber > highestNumber)
                highestNumber = idNumber;

            activity.transitions.forEach(transition => {
                const idNumberTran = Number(transition.id.replace("ID_", ""));
                if (idNumberTran > highestNumber)
                    highestNumber = idNumberTran;
            });
        });

        return `ID_${highestNumber + 1}`;
    }
}
